import { Avatar, AvatarFallback } from "@/shared/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import { ChevronDown, LogOut, User, ChevronLeft, KeyRound } from "lucide-react";
import { DefaultLogo } from "@/features/layout/components/DefaultLogo";
import { ThemeToggle } from "@/shared/components/ui/themeToggle";
import { useLogout } from "@/features/login/hooks/useLogout";
import { useNavigate, useLocation } from "react-router";
import { LogoClienteFinal } from "@/shared/components/LogoClienteFinal";
import { useLogoMarcaTema } from "@/shared/hooks/useLogoMarcaTema";
import defaultLogo from "@/assets/LOGO-RETRO-IA.png";
import { useState } from "react";
import { ChangePasswordDialog } from "@retro-ia/shared/password";

export function HeaderBranding() {
  const { usuarioInfo } = useCurrentUser();
  const { mutate: logout, isPending } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const logoMarca = useLogoMarcaTema(usuarioInfo?.aprendiz?.clienteFinal);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const handleGoBack = () => {
    const path = location.pathname;
    if (path.startsWith("/actividad/")) {
      navigate("/programas");
    } else if (path.startsWith("/videollamada/")) {
      const actividadId = location.state?.actividadId;
      if (actividadId) {
        navigate(`/actividad/${actividadId}`, {
          state: location.state,
        });
      } else {
        navigate("/programas");
      }
    } else {
      navigate(-1);
    }
  };

  // El BrandingProvider ya aplicó --primary y --secondary al :root
  const showBackButton = location.pathname !== "/programas";

  return (
    <header
      className="w-full flex items-center justify-between px-6 py-4 border-b shadow-sm"
      style={{
        background: "linear-gradient(90deg, #7c3aed, #3b82f6)",
      }}
    >
      <div className="flex items-center gap-3">
        {logoMarca ? (
          <LogoClienteFinal
            logoMarca={logoMarca}
            defaultSrc={defaultLogo}
            alt={usuarioInfo?.aprendiz?.clienteFinal?.nombreEmpresa + " logo"}
            className="h-12 w-auto mb-1 rounded shadow cursor-pointer"
            onClick={() => navigate("/programas")}
          />
        ) : (
          <div className="cursor-pointer" onClick={() => navigate("/programas")}>
            <DefaultLogo className="h-12 w-auto" />
          </div>
        )}
        {showBackButton && (
          <button
            className="flex items-center px-2 py-1 rounded bg-white/50 hover:bg-white text-primary shadow cursor-pointer"
            onClick={() => handleGoBack()}
            title="Volver atrás"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span className="hidden sm:inline">Atrás</span>
          </button>
        )}
      </div>
      <div className="flex items-center gap-10">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-3 focus:outline-none">
            <Avatar>
              <AvatarFallback>
                {usuarioInfo?.nombre?.[0]}
                {usuarioInfo?.apellidoPaterno?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-foreground">
                {usuarioInfo?.nombre} {usuarioInfo?.apellidoPaterno}
              </p>
              <p className="text-xs text-muted-foreground">
                {usuarioInfo?.correo}
              </p>
            </div>
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            className="w-56 bg-[var(--background)] border border-border shadow-md"
          >
            <DropdownMenuLabel className="flex items-center gap-2">
              <User className="w-4 h-4" /> Perfil
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setChangePasswordOpen(true)}
              className="cursor-pointer hover:bg-[var(--muted)]"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Cambiar contraseña
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => logout()}
              disabled={isPending}
              className="cursor-pointer hover:bg-[var(--muted)]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {isPending ? "Cerrando..." : "Cerrar sesión"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ThemeToggle />
      </div>
      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />
    </header>
  );
}
